// src/components/NotificationBar/NotificationBar.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, Info, AlertTriangle, CheckCircle, XCircle, Tag } from 'lucide-react';
import styles from './NotificationBar.module.css';

const NotificationBar = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = import.meta.env.VITE_API_URL;

  const fetchNotifications = useCallback(async () => {
    if (!API_BASE) {
      console.warn('API base URL is not set');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/notifications/active`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data?.success && Array.isArray(data.notifications) && data.notifications.length > 0) {
        setNotifications(data.notifications);
        setIsVisible(true);
        setError(null);
      } else {
        setNotifications([]);
        setIsVisible(false);
      }
    } catch (err) {
      console.error('Error in NotificationBar:', err);
      setError('Failed to load notifications');
      setIsVisible(false);
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (notifications.length > 1 && !isPaused && !isLoading && !error) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % notifications.length);
      }, 10000); // Change notification every 10 seconds
      return () => clearInterval(interval);
    }
  }, [notifications.length, isPaused, isLoading, error]);

  // Don't render anything if there are no notifications or if there's an error
  if (!isVisible || notifications.length === 0 || error) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    // Store in localStorage to remember user dismissed it for this session
    localStorage.setItem('notificationBarDismissed', Date.now().toString());
  };

  const handleNotificationClick = (notification) => {
    if (notification.link) {
      if (notification.link.startsWith('http')) {
        window.open(notification.link, '_blank');
      } else {
        window.location.href = notification.link;
      }
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle size={16} />;
      case 'success': return <CheckCircle size={16} />;
      case 'error': return <XCircle size={16} />;
      case 'promotion': return <Tag size={16} />;
      default: return <Info size={16} />;
    }
  };

  const getTypeClass = (type) => {
    switch (type) {
      case 'warning': return styles.warning;
      case 'success': return styles.success;
      case 'error': return styles.error;
      case 'promotion': return styles.promotion;
      default: return styles.info;
    }
  };

  if (!isVisible || notifications.length === 0) {
    return null;
  }

  const currentNotification = notifications[currentIndex];

  return (
    <div 
      className={`${styles.notificationBar} ${getTypeClass(currentNotification.type)}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.icon}>
            {getIcon(currentNotification.type)}
          </div>
          
          <div 
            className={`${styles.message} ${currentNotification.link ? styles.clickable : ''}`}
            onClick={() => handleNotificationClick(currentNotification)}
          >
            <span className={styles.scrollingText}>
              {currentNotification.message}
            </span>
            {currentNotification.link && (
              <ExternalLink size={14} className={styles.linkIcon} />
            )}
          </div>

          {notifications.length > 1 && (
            <div className={styles.indicators}>
              {notifications.map((_, index) => (
                <span
                  key={index}
                  className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </div>
          )}
        </div>

        <button 
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Dismiss notification"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default NotificationBar;